import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { useCompanyProfile } from '../../hooks/useCompanyProfile';
import TrialBanner from '../../components/broker/TrialBanner';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend } from 'recharts';

interface Stats {
  totalCarriers: number;
  pendingDocuments: number;
  verifiedDocuments: number;
  rejectedDocuments: number;
}

interface CarrierStatusData {
  approved: number;
  pending: number;
  rejected: number;
  incomplete: number;
}

interface InsuranceData {
  expired: number;
  within30: number;
  within60: number;
  within90: number;
  valid: number;
}

export default function BrokerDashboardPage() {
  const location = useLocation();
  const { profile } = useCompanyProfile();
  const [stats, setStats] = useState<Stats>({ totalCarriers: 0, pendingDocuments: 0, verifiedDocuments: 0, rejectedDocuments: 0 });
  const [carrierStatus, setCarrierStatus] = useState<CarrierStatusData>({ approved: 0, pending: 0, rejected: 0, incomplete: 0 });
  const [insuranceData, setInsuranceData] = useState<InsuranceData>({ expired: 0, within30: 0, within60: 0, within90: 0, valid: 0 });
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      setTimeout(() => setSuccessMessage(''), 5000);
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  useEffect(() => {
    if (profile) loadStats();
  }, [profile]);

  const loadStats = async () => {
    try {
      if (!profile?.company_code) { setLoading(false); return; }

      // Get carriers via applications
      const { data: applications } = await supabase
        .from('applications')
        .select('user_id, status, form_data')
        .eq('company_code', profile.company_code);
        console.log('Dashboard - company_code:', profile.company_code, 'applications:', applications);

      const carrierIds = applications?.map(a => a.user_id) || [];

      // Carrier status counts
      const approved = applications?.filter(a => a.status === 'approved').length || 0;
      const pending = applications?.filter(a => a.status === 'pending' || a.status === 'submitted').length || 0;
      const rejected = applications?.filter(a => a.status === 'rejected').length || 0;
      const incomplete = applications?.filter(a => !a.status || a.status === 'incomplete').length || 0;

      setCarrierStatus({ approved, pending, rejected, incomplete });

      // Insurance expiration analysis
      const today = new Date();
      const in30 = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000);
      const in60 = new Date(today.getTime() + 60 * 24 * 60 * 60 * 1000);
      const in90 = new Date(today.getTime() + 90 * 24 * 60 * 60 * 1000);

      let expired = 0, within30 = 0, within60 = 0, within90 = 0, valid = 0;

      (applications || []).forEach(app => {
        const expDate = app.form_data?.insurance?.expirationDate;
        if (!expDate) return;
        const exp = new Date(expDate);
        if (exp < today) expired++;
        else if (exp <= in30) within30++;
        else if (exp <= in60) within60++;
        else if (exp <= in90) within90++;
        else valid++;
      });

      setInsuranceData({ expired, within30, within60, within90, valid });

      // Document stats
      const { data: documents } = await supabase
        .from('documents')
        .select('status')
        .in('user_id', carrierIds.length > 0 ? carrierIds : ['none']);

      const pendingDocs = documents?.filter(d => d.status === 'pending').length || 0;
      const verifiedDocs = documents?.filter(d => d.status === 'verified').length || 0;
      const rejectedDocs = documents?.filter(d => d.status === 'rejected').length || 0;

      setStats({
        totalCarriers: carrierIds.length,
        pendingDocuments: pendingDocs,
        verifiedDocuments: verifiedDocs,
        rejectedDocuments: rejectedDocs,
      });
    } catch (err) {
      console.error('Error loading stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const copyCompanyCode = () => {
    navigator.clipboard.writeText(profile?.company_code || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Chart data
  const donutData = [
    { name: 'Approved', value: carrierStatus.approved, color: '#22c55e' },
    { name: 'Pending', value: carrierStatus.pending, color: '#f59e0b' },
    { name: 'Rejected', value: carrierStatus.rejected, color: '#ef4444' },
    { name: 'Incomplete', value: carrierStatus.incomplete, color: '#d1d5db' },
  ].filter(d => d.value > 0);

  const insuranceChartData = [
    { name: 'Expired', count: insuranceData.expired, fill: '#ef4444' },
    { name: '30 Days', count: insuranceData.within30, fill: '#f97316' },
    { name: '60 Days', count: insuranceData.within60, fill: '#f59e0b' },
    { name: '90 Days', count: insuranceData.within90, fill: '#22c55e' },
    { name: 'Valid', count: insuranceData.valid, fill: '#3b82f6' },
  ];

  const totalCarriers = carrierStatus.approved + carrierStatus.pending + carrierStatus.rejected + carrierStatus.incomplete;
  const fullyCompliant = totalCarriers > 0 ? carrierStatus.approved - insuranceData.expired : 0;
  const complianceRate = totalCarriers > 0 ? Math.round((Math.max(0, fullyCompliant) / totalCarriers) * 100) : 0;

  if (loading) return <div className="text-xl">Loading...</div>;

  return (
    <div>
      {successMessage && (
        <div className="bg-green-50 border-l-4 border-green-500 p-4 mb-6">
          <div className="flex items-center">
            <svg className="h-5 w-5 text-green-500 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <p className="text-sm font-medium text-green-800">{successMessage}</p>
          </div>
        </div>
      )}

      {(profile?.subscription_status === 'trial' || profile?.subscription_status === 'expired') && profile?.trial_ends_at && (
        <TrialBanner
          trialEndsAt={profile.trial_ends_at}
          subscriptionStatus={profile.subscription_status || 'trial'}
          carrierCount={stats.totalCarriers}
          carrierLimit={profile.carrier_limit || 20}
        />
      )}

      <h1 className="text-3xl font-bold mb-8">Broker Dashboard</h1>

      {/* Company Code Card */}
      <div className="bg-white p-6 rounded-lg shadow mb-6">
        <h2 className="font-semibold text-lg">Your Company Code</h2>
        <p className="text-3xl font-mono mt-2">{profile?.company_code}</p>
        <button onClick={copyCompanyCode} className="mt-4 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
          {copied ? 'Copied!' : 'Copy Code'}
        </button>
        <p className="text-sm text-gray-600 mt-2">Share this code with carriers to join your company</p>
      </div>

      {/* KPI Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
        <div className="bg-white shadow rounded-lg p-5 border-t-4 border-blue-500">
          <p className="text-gray-500 text-sm font-medium">Total Carriers</p>
          <h3 className="text-3xl font-bold text-gray-900 mt-1">{totalCarriers}</h3>
          <p className="text-xs text-gray-400 mt-1">{profile?.carrier_limit ? `${profile.carrier_limit - totalCarriers} slots left` : ''}</p>
        </div>
        <div className="bg-white shadow rounded-lg p-5 border-t-4 border-green-500">
          <p className="text-gray-500 text-sm font-medium">Approved</p>
          <h3 className="text-3xl font-bold text-green-600 mt-1">{carrierStatus.approved}</h3>
          <p className="text-xs text-green-500 mt-1">Active & compliant</p>
        </div>
        <div className="bg-white shadow rounded-lg p-5 border-t-4 border-yellow-500">
          <p className="text-gray-500 text-sm font-medium">Pending</p>
          <h3 className="text-3xl font-bold text-yellow-600 mt-1">{carrierStatus.pending}</h3>
          <p className="text-xs text-yellow-500 mt-1">Awaiting review</p>
        </div>
        <div className="bg-white shadow rounded-lg p-5 border-t-4 border-red-500">
          <p className="text-gray-500 text-sm font-medium">Rejected</p>
          <h3 className="text-3xl font-bold text-red-600 mt-1">{carrierStatus.rejected}</h3>
          <p className="text-xs text-red-500 mt-1">Needs attention</p>
        </div>
        <div className="bg-white shadow rounded-lg p-5 border-t-4 border-purple-500">
          <p className="text-gray-500 text-sm font-medium">Compliance Rate</p>
          <h3 className="text-3xl font-bold text-purple-600 mt-1">{complianceRate}%</h3>
          <p className="text-xs text-purple-400 mt-1">Approved / Total</p>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">

        {/* Carrier Status Donut Chart */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Carrier Status</h2>
          {totalCarriers === 0 ? (
            <div className="flex items-center">
              <div className="w-1/2" style={{ height: 256 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={[{ name: 'Empty', value: 1 }]} cx="50%" cy="50%" innerRadius={60} outerRadius={90} dataKey="value">
                      <Cell fill="#e5e7eb" />
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="w-1/2 pl-4 text-sm text-gray-400">
                <p>No carriers yet.</p>
                <p className="mt-1">Share your company code to get started.</p>
              </div>
            </div>
          ) : (
            <div className="flex items-center">
             <div className="w-1/2" style={{ height: 256 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={donutData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={3}
                      dataKey="value"
                    >
                      {donutData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                   <Tooltip formatter={(value: any) => [value, 'Carriers']} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="w-1/2 space-y-3 pl-4">
                {[
                  { label: 'Approved', value: carrierStatus.approved, color: 'bg-green-500' },
                  { label: 'Pending', value: carrierStatus.pending, color: 'bg-yellow-500' },
                  { label: 'Rejected', value: carrierStatus.rejected, color: 'bg-red-500' },
                  { label: 'Incomplete', value: carrierStatus.incomplete, color: 'bg-gray-300' },
                ].map(item => (
                  <div key={item.label} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${item.color}`} />
                      <span className="text-sm text-gray-700">{item.label}</span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900">{item.value}</span>
                  </div>
                ))}
                <div className="pt-2 border-t">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Compliance</span>
                    <span className={`text-sm font-bold ${complianceRate >= 70 ? 'text-green-600' : complianceRate >= 40 ? 'text-yellow-600' : 'text-red-600'}`}>
                      {complianceRate}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Insurance Expiration Bar Chart */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Insurance Risk Monitor</h2>
         {totalCarriers === 0 ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={[{ name: 'Expired', count: 0 }, { name: '30 Days', count: 0 }, { name: '60 Days', count: 0 }, { name: '90 Days', count: 0 }, { name: 'Valid', count: 0 }]} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                  <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                    {[{ fill: '#ef4444' }, { fill: '#f97316' }, { fill: '#f59e0b' }, { fill: '#22c55e' }, { fill: '#3b82f6' }].map((entry, i) => (
                      <Cell key={`empty-${i}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div style={{ height: 256 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={insuranceChartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                  <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(value: any) => [value, 'Carriers']} />
                  <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                    {insuranceChartData.map((entry, index) => (
                      <Cell key={`bar-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
          {insuranceData.expired > 0 && (
            <div className="mt-3 bg-red-50 border border-red-200 rounded p-3">
              <p className="text-sm text-red-700 font-medium">⚠ {insuranceData.expired} carrier(s) have expired insurance</p>
            </div>
          )}
        </div>
      </div>

  {/* Document Stats with Chart */}
      <div className="bg-white shadow rounded-lg p-6">
       <h2 className="text-lg font-semibold text-gray-800 mb-4">Document Overview</h2>
        <div className="flex items-center">
          <div style={{ height: 200 }} className="w-1/2">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={[
                { name: 'Pending', count: stats.pendingDocuments, fill: '#f59e0b' },
                { name: 'Verified', count: stats.verifiedDocuments, fill: '#22c55e' },
                { name: 'Rejected', count: stats.rejectedDocuments, fill: '#ef4444' },
              ]} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                <Tooltip formatter={(value: any) => [value, 'Documents']} />
                <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                  {[{ fill: '#f59e0b' }, { fill: '#22c55e' }, { fill: '#ef4444' }].map((entry, i) => (
                    <Cell key={`doc-${i}`} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="w-1/2 pl-6 space-y-4">
            <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-yellow-500" />
                <span className="text-sm font-medium text-gray-700">Pending</span>
              </div>
              <span className="text-xl font-bold text-yellow-600">{stats.pendingDocuments}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-500" />
                <span className="text-sm font-medium text-gray-700">Verified</span>
              </div>
              <span className="text-xl font-bold text-green-600">{stats.verifiedDocuments}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <span className="text-sm font-medium text-gray-700">Rejected</span>
              </div>
              <span className="text-xl font-bold text-red-600">{stats.rejectedDocuments}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}